import { useState } from "react";
import { Link } from "react-router-dom";
import type { Product } from "../../content/products";
import { SiteIcon } from "./SiteIcon";

interface SiteImageProps {
  src: string;
  alt: string;
  className?: string;
  /** Above-the-fold images load eagerly; everything else is lazy. */
  eager?: boolean;
}

/**
 * Image that degrades to the sandstone tile behind it: the container keeps its fixed aspect ratio
 * while loading (no layout shift) and, if the file fails, the image is hidden rather than showing a
 * broken-image glyph — the text over it stays legible (home States: Loading / Error).
 */
export function SiteImage({ src, alt, className, eager = false }: SiteImageProps) {
  const [failed, setFailed] = useState(false);
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      onError={() => setFailed(true)}
      data-failed={failed || undefined}
      style={failed ? { visibility: "hidden" } : undefined}
    />
  );
}

interface ProductCardProps {
  product: Product;
  /** "card" = photo card with overlaid text (home bento); "tile" = photo above text (grids). */
  variant?: "card" | "tile";
  /** The home bento's large lead card (BR-WEB-001): spans two rows, then two columns, as the container narrows. */
  large?: boolean;
}

/** A product linking to /products/{slug}. */
export function ProductCard({ product, variant = "card", large = false }: ProductCardProps) {
  const to = `/products/${product.slug}`;
  if (variant === "tile") {
    return (
      <Link className="gw-tile" to={to}>
        <div className="gw-tile-img">
          <SiteImage src={product.hero} alt={product.name} />
        </div>
        <div className="gw-tile-b">
          <span className="gw-cat">{product.category}</span>
          <h3>{product.name}</h3>
          <p>{product.tagline}</p>
        </div>
      </Link>
    );
  }
  return (
    <Link className={large ? "gw-card gw-card-lg" : "gw-card"} to={to} data-size={large ? "large" : undefined}>
      <SiteImage src={product.hero} alt={`${product.name} by Greatway Patios`} />
      <div className="gw-card-body">
        <span className="gw-cat">{product.category}</span>
        <h3>{product.name}</h3>
        <p>{product.tagline}</p>
        <span className="gw-more">
          Explore <SiteIcon name="arrow" size={16} />
        </span>
      </div>
    </Link>
  );
}
